import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * The cart count is always synchronized with Shopify's real cart state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * Current visible cart count.
   * @returns {number}
   */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
  }

  /**
   * Sets the visible cart count.
   * @param {number} value
   */
  set currentCartCount(value) {
    const safeValue = Math.max(0, Number(value) || 0);

    this.refs.cartBubbleCount.textContent =
      safeValue > 0 && safeValue < 100 ? String(safeValue) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    /*
     * Observe the cart drawer because some apps can modify the cart
     * without dispatching Horizon's normal cartUpdate event.
     */
    this.observeCartDrawer();

    /*
     * Never trust the number rendered/stored previously.
     * Get the real cart count immediately.
     */
    this.syncCartCount(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    this.cartDrawerObserver?.disconnect();

    clearTimeout(this.cartSyncTimeout);
    clearTimeout(this.cartVerificationTimeout);
  }

  /**
   * Synchronizes when returning to a cached page.
   */
  onPageShow = () => {
    this.scheduleCartSync(false, 0);
  };

  /**
   * Synchronizes when the customer comes back to the tab.
   */
  onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.scheduleCartSync(false, 0);
    }
  };

  /**
   * Handles Horizon cart update events.
   *
   * We deliberately ignore event.detail.itemCount because some
   * integrations send deltas rather than the final cart quantity.
   */
  onCartUpdate = () => {
    /*
     * First quick synchronization.
     */
    this.scheduleCartSync(true, 120);

    /*
     * Second verification after apps such as gift/bundle apps
     * have finished adding or removing automatic items.
     */
    clearTimeout(this.cartVerificationTimeout);

    this.cartVerificationTimeout = setTimeout(() => {
      this.syncCartCount(false);
    }, 1200);
  };

  /**
   * Debounces requests to Shopify's cart.
   *
   * @param {boolean} animate
   * @param {number} delay
   */
  scheduleCartSync = (animate = false, delay = 150) => {
    clearTimeout(this.cartSyncTimeout);

    this.cartSyncTimeout = setTimeout(() => {
      this.syncCartCount(animate);
    }, delay);
  };

  /**
   * Fetches the REAL cart state directly from Shopify.
   *
   * This is the only source of truth for the header cart badge.
   *
   * @param {boolean} animate
   */
  syncCartCount = async (animate = false) => {
    /*
     * Increment request id so an older/slower response can never
     * overwrite a newer cart state.
     */
    this.cartSyncRequestId = (this.cartSyncRequestId ?? 0) + 1;

    const requestId = this.cartSyncRequestId;

    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      const cart = await response.json();

      /*
       * Ignore this response if a more recent sync started meanwhile.
       */
      if (requestId !== this.cartSyncRequestId) return;

      const itemCount = Math.max(0, Number(cart.item_count) || 0);

      await this.renderCartBubble(itemCount, false, animate);
    } catch (error) {
      console.warn('[cart-icon] Cart count synchronization failed:', error);
    }
  };

  /**
   * Watches the Shopify cart drawer for content changes.
   *
   * This catches quantity changes and removals even when a third-party
   * app does not dispatch Horizon's cartUpdate event.
   */
  observeCartDrawer = () => {
    if (!document.body) return;

    this.cartDrawerObserver?.disconnect();

    this.cartDrawerObserver = new MutationObserver((mutations) => {
      const cartDrawerChanged = mutations.some((mutation) => {
        /*
         * Mutation happened inside the cart drawer.
         */
        if (
          mutation.target instanceof Element &&
          mutation.target.closest(
            '#cart-drawer, #shopify-section-cart-drawer-section, cart-drawer-component'
          )
        ) {
          return true;
        }

        /*
         * Cart drawer itself may have been replaced/re-rendered.
         */
        const changedNodes = [
          ...mutation.addedNodes,
          ...mutation.removedNodes,
        ];

        return changedNodes.some((node) => {
          if (!(node instanceof Element)) return false;

          return (
            node.matches?.(
              '#cart-drawer, #shopify-section-cart-drawer-section, cart-drawer-component'
            ) ||
            node.querySelector?.(
              '#cart-drawer, #shopify-section-cart-drawer-section, cart-drawer-component'
            )
          );
        });
      });

      if (cartDrawerChanged) {
        /*
         * Wait briefly for Shopify / Koala to finish changing the cart.
         */
        this.scheduleCartSync(false, 180);
      }
    });

    this.cartDrawerObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  /**
   * Renders the cart bubble.
   *
   * IMPORTANT:
   * We always SET the final number.
   * We never add a delta to the previous number.
   *
   * @param {number} itemCount
   * @param {boolean} comingFromProductForm
   * @param {boolean} animate
   */
  renderCartBubble = async (
    itemCount,
    comingFromProductForm = false,
    animate = true
  ) => {
    /*
     * comingFromProductForm is intentionally kept in the signature
     * for compatibility, but is NOT used to increment the count.
     */
    void comingFromProductForm;

    const safeItemCount = Math.max(0, Number(itemCount) || 0);
    const previousCount = this.currentCartCount;

    /*
     * Always use Shopify's absolute cart quantity.
     */
    this.currentCartCount = safeItemCount;

    /*
     * Hide everything when cart is empty.
     */
    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      safeItemCount === 0
    );

    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      safeItemCount === 0
    );

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      safeItemCount > 0
    );

    /*
     * Keep storage correct if another part of the theme reads it,
     * but storage is NEVER used as our source of truth.
     */
    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(safeItemCount),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // Storage unavailable: no problem.
    }

    /*
     * No animation when empty, unchanged, or explicitly disabled.
     */
    if (
      !animate ||
      safeItemCount === 0 ||
      safeItemCount === previousCount
    ) {
      return;
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');

    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Kept for compatibility with older calls in the theme.
   *
   * Instead of trusting sessionStorage, always ask Shopify.
   */
  ensureCartBubbleIsCorrect = () => {
    this.scheduleCartSync(false, 0);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}